import { Box, CircularProgress, Icon, IconButton as MuiIconButton, Tooltip } from '@mui/material';
import type { MouseEventHandler } from 'react';

import { useColors } from '../ThemeProvider';

interface Props {
  ariaLabel: string;
  disableTooltip?: boolean;
  disabled?: boolean;
  icon: React.ElementType;
  iconColor?: string;
  iconSize?: 'small' | 'medium' | 'large';
  isLoading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  addBackground?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export function IconButton({
  ariaLabel,
  disableTooltip = false,
  disabled = false,
  icon,
  iconColor,
  iconSize = 'medium',
  isLoading = false,
  onClick = () => {},
  addBackground = false,
  size = 'large',
}: Props) {
  const { textSecondary, surface, textDisabled } = useColors();
  const isDisabled = disabled || isLoading;

  return (
    <Tooltip
      title={disableTooltip ? '' : ariaLabel}
      disableHoverListener={disableTooltip}
      disableFocusListener={disableTooltip}
      disableTouchListener={disableTooltip}
    >
      <div style={{ position: 'relative' }}>
        <MuiIconButton
          disabled={isDisabled}
          onClick={onClick}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={ariaLabel}
          aria-busy={isLoading}
          size={size}
          sx={{
            backgroundColor: addBackground ? surface : 'transparent',
            height: size === 'small' ? '2rem' : size === 'medium' ? '2.5rem' : '3.2rem',
            width: size === 'small' ? '2rem' : size === 'medium' ? '2.5rem' : '3.2rem',
          }}
        >
          <Icon
            component={icon}
            fontSize={iconSize}
            sx={{ color: isDisabled ? textDisabled : iconColor || textSecondary }}
          />
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: '4px',
                display: 'grid',
                placeItems: 'center',
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size="100%" aria-label={ariaLabel} />
            </Box>
          )}
        </MuiIconButton>
      </div>
    </Tooltip>
  );
}
